import { useState } from 'react';
import { PermissionDenied } from '@/components/RequirePermission';
import { useAuth } from '@/auth/AuthContext';
import { useT } from '@/i18n/LanguageContext';
import { CountFlagsTab } from './CountFlags';
import { ShortfallsTab } from './Shortfalls';
import { HistoryTab } from './History';

type Tab = 'count' | 'shortfalls' | 'history';

/**
 * "Исправления" - everything a supervisor must fix: stock that needs a
 * count, outbound order lines that cannot be completed, and the history of
 * every correction (who, what, why). Visibility of each tab follows the
 * read permission behind it; the action buttons inside need WRITE.
 */
export function ExceptionsPage(): JSX.Element {
  const t = useT();
  const { hasPermission } = useAuth();
  const canInventory = hasPermission('INVENTORY.READ');
  const canOrders = hasPermission('ORDERS.READ');
  const tabs: { id: Tab; label: string }[] = [];
  if (canInventory) tabs.push({ id: 'count', label: t('exc.tabCount') });
  if (canOrders) tabs.push({ id: 'shortfalls', label: t('exc.tabShortfalls') });
  if (canInventory) tabs.push({ id: 'history', label: t('exc.tabHistory') });
  const [selected, setSelected] = useState<Tab | null>(null);
  const tab = selected && tabs.some((x) => x.id === selected) ? selected : (tabs[0]?.id ?? null);

  if (!tab) {
    return (
      <div>
        <div className="page-header">
          <h1>{t('exc.title')}</h1>
        </div>
        <PermissionDenied reason={t('exc.noAccess')} />
      </div>
    );
  }

  return (
    <div>
      <div className="page-header">
        <h1>{t('exc.title')}</h1>
      </div>
      <div className="tabs" role="tablist" aria-label={t('exc.title')}>
        {tabs.map((x) => (
          <button
            key={x.id}
            type="button"
            role="tab"
            aria-selected={tab === x.id}
            className={tab === x.id ? 'active' : ''}
            onClick={() => setSelected(x.id)}
          >
            {x.label}
          </button>
        ))}
      </div>
      {tab === 'count' && <CountFlagsTab />}
      {tab === 'shortfalls' && <ShortfallsTab />}
      {tab === 'history' && <HistoryTab />}
    </div>
  );
}
